import React from "react";
import { motion } from "framer-motion";
import PropTypes from "prop-types";
import { useCartStore } from "../store/cartStore";
import { RiArrowLeftSLine, RiArrowRightSLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import CartOverlayHeader from "./cart-overlay/CartOverlayHeader";
import CartItemRow from "./cart-overlay/CartItemRow";
import CartSummary from "./cart-overlay/CartSummary";
import { buildFoonPath } from "../utils/routes";
import { useProducts } from "../hook/useProducts";
import { useCurrencyContext } from "../lib/CurrencyContext";
import { useCheckout } from "../hook/useCheckout";

const CartOverlay = ({ isOpen, onClose }) => {
  const { cartItems, addToCart, removeFromCart, updateCartItemQuantity } =
    useCartStore();
  const navigate = useNavigate();
  const [recommendedIndex, setRecommendedIndex] = React.useState(0);
  const { products } = useProducts();
  const { formatProductPrice, currency, rates } = useCurrencyContext();
  const { submitOrder, isSubmitting, error } = useCheckout(currency, rates);

  const itemCount = cartItems.length;

  const subtotal = cartItems.reduce((acc, item) => {
    const price = Number(String(item.price).replace(/[^0-9.]/g, "")) || 0;
    return acc + price * (item.quantity ?? 0);
  }, 0);
  const totalLabel = Number.isInteger(subtotal)
    ? String(subtotal)
    : subtotal.toFixed(2);

  const recommendedProducts = [
    products.find((product) => product.id === 3),
    ...products.filter((product) => product.id !== 3),
  ]
    .filter(Boolean)
    .slice(0, 3);
  const activeRecommendedProduct = recommendedProducts[recommendedIndex];
  const recommendedColor =
    activeRecommendedProduct?.colors &&
    Object.keys(activeRecommendedProduct.colors)[0];

  const handleShopNow = () => {
    navigate(buildFoonPath("/store"));
    onClose();
  };

  const handleCheckoutClick = () => {
    submitOrder();
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed inset-0 z-50 max-h-[800px] flex justify-end max-w-[1440px] mx-auto lg:!overflow-clip"
      >
        {/* Opaque Background */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="absolute inset-0 bg-black/70 max-md:hidden backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Sidebar */}
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="relative w-full lg:!max-w-md h-dvh lg:!h-full bg-white shadow-2xl flex flex-col font-['Helvetica_Neue',sans-serif] z-50"
        >
          <CartOverlayHeader itemCount={itemCount} onClose={onClose} />

          <div className="flex-[1] overflow-y-auto px-6 flex flex-col">
            {cartItems.length === 0 ? (
              <div className="flex-1 -mx-6 flex flex-col">
                <div className="flex-1 min-h-[360px] flex flex-col items-center justify-center px-6 text-center">
                  <p className="text-[20px] font-light text-[#2f2f2f]">
                    Your Cart is empty.
                  </p>
                  <button
                    onClick={handleShopNow}
                    className="mt-8 min-w-[214px] h-[58px] bg-black text-white border border-black font-mono uppercase text-[15px] transition-colors hover:bg-white hover:text-black"
                  >
                    Shop Now
                  </button>
                </div>

                <div
                  className="px-6 py-8"
                  style={{
                    borderTop: "1px solid #DDDCDC",
                  }}
                >
                  <div className="flex items-center justify-between gap-4">
                    <h3 className="text-[16px] font-light text-[#2f2f2f]">
                      Discover More
                    </h3>

                    {recommendedProducts.length > 1 && (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          aria-label="Show previous product"
                          onClick={() =>
                            setRecommendedIndex((currentIndex) =>
                              currentIndex === 0
                                ? recommendedProducts.length - 1
                                : currentIndex - 1,
                            )
                          }
                          className="h-9 w-9 border border-[#e2e2e2] flex items-center justify-center text-[#8b8b8b] transition-colors hover:border-black hover:text-black"
                        >
                          <RiArrowLeftSLine className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          aria-label="Show next product"
                          onClick={() =>
                            setRecommendedIndex((currentIndex) =>
                              currentIndex === recommendedProducts.length - 1
                                ? 0
                                : currentIndex + 1,
                            )
                          }
                          className="h-9 w-9 border border-[#e2e2e2] flex items-center justify-center text-[#8b8b8b] transition-colors hover:border-black hover:text-black"
                        >
                          <RiArrowRightSLine className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                  </div>

                  {activeRecommendedProduct && (
                    <motion.div
                      key={activeRecommendedProduct.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className="mt-10 flex items-center gap-16"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          onClose();
                          navigate(buildFoonPath(`/product/${activeRecommendedProduct.id}`));
                        }}
                        className="w-[124px] h-[124px] flex items-center justify-center shrink-0"
                      >
                        <img
                          src={
                            activeRecommendedProduct.defaultImage ||
                            (typeof activeRecommendedProduct.image === "object"
                              ? Object.values(
                                  activeRecommendedProduct.image,
                                )[0]?.[0]
                              : Array.isArray(activeRecommendedProduct.image)
                                ? activeRecommendedProduct.image[0]
                                : "")
                          }
                          alt={activeRecommendedProduct.name}
                          className="w-full h-full object-contain"
                        />
                      </button>

                      <div className="flex-1 flex flex-col items-start">
                        <button
                          type="button"
                          onClick={() => {
                            onClose();
                            navigate(buildFoonPath(`/product/${activeRecommendedProduct.id}`));
                          }}
                          className="text-left text-[14px] font-mono leading-[1.35] uppercase text-[#2f2f2f]"
                        >
                          {activeRecommendedProduct.name}
                        </button>
                        <span className="mt-3 text-[14px] text-[#2f2f2f]">
                          {formatProductPrice(activeRecommendedProduct.price)}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            addToCart({
                              ...activeRecommendedProduct,
                              selectedColor: recommendedColor,
                            })
                          }
                          className="mt-5 w-full h-[44px] border border-[#cfcfcf] font-mono uppercase text-[14px] lg:!tracking-[0.3em] text-[#3a3a3a] transition-colors hover:border-black hover:text-black"
                        >
                          Add to Bag
                        </button>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            ) : (
              <div className="divide-y divide-[#e2e2e2]">
                {cartItems.map((item) => (
                  <CartItemRow
                    key={`${item.id}-${item.selectedColor}`}
                    item={{...item, price: formatProductPrice(item.price)}}
                    onDecrement={() =>
                      updateCartItemQuantity(item.id, -1, item.selectedColor)
                    }
                    onIncrement={() =>
                      updateCartItemQuantity(item.id, 1, item.selectedColor)
                    }
                    onRemove={() => removeFromCart(item.id, item.selectedColor)}
                  />
                ))}
              </div>
            )}
          </div>

          {cartItems.length > 0 && (
            <CartSummary
              total={`$${totalLabel}`}
              onCheckout={handleCheckoutClick}
              isSubmitting={isSubmitting}
              error={error}
            />
          )}
        </motion.div>
      </motion.div>
    </>
  );
};

CartOverlay.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CartOverlay;
